import * as React from "react";
import { useMemo, useState, useRef, useEffect } from "react";
import {
  GripVertical,
  ArrowLeftToLine,
  ArrowRightToLine,
  X,
  ChevronRight,
} from "lucide-react";
import { Button } from "../../ui/button";
import { ScrollArea } from "../../ui/scroll-area";
import TooltipWrapper from "../../common/table/TooltipWrapper";

const PreferencesPanel = ({ isOpen, onClose, table, setColumnOrder }) => {
  const allColumns = table.getAllColumns();
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // this is to handle drag and drop when there are a lot of them
  const autoScrollIntervalRef = useRef(null);
  const modalContentRef = useRef(null);

  const { columnOrder } = table.getState();

  // const stickyColumnsState = useMemo(() => {
  //   return table.options.state?.columnPinning;
  // }, [table.options.state?.stickyColumns]);

  const [stickyColumnsState, setStickyColumnsState] = useState({
    left: table.options.state?.columnPinning?.left || [],
    right: table.options.state?.columnPinning?.right || [],
  });

  const defaultColumnOrder = useMemo(() => {
    return allColumns.map((col) => col.id);
  }, [allColumns]);

  const sortedColumns = useMemo(() => {
    return [...allColumns].sort((a, b) => {
      const aIndex = columnOrder.indexOf(a.id);
      const bIndex = columnOrder.indexOf(b.id);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  }, [allColumns, columnOrder]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        modalContentRef.current &&
        !modalContentRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    document.addEventListener("click", handleClickOutside, true);

    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [onClose, isOpen]);

  // this is to handle drag and drop when there are a lot of columns
  const handleAutoScroll = (e) => {
    if (!modalContentRef.current) return;

    const container = modalContentRef.current;
    const containerRect = container.getBoundingClientRect();
    const mouseY = e.clientY;
    const threshold = 60;
    const scrollStep = 8;

    clearInterval(autoScrollIntervalRef.current);

    const distanceFromTop = mouseY - containerRect.top;
    const distanceFromBottom = containerRect.bottom - mouseY;

    if (distanceFromTop < threshold || distanceFromBottom < threshold) {
      autoScrollIntervalRef.current = setInterval(() => {
        if (distanceFromTop < threshold) {
          container.scrollTop -= scrollStep;
        } else if (distanceFromBottom < threshold) {
          container.scrollTop += scrollStep;
        }
      }, 16);
    }
  };

  const stopAutoScroll = () => {
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => stopAutoScroll();
  }, []);

  const handleDragStart = (e, column) => {
    setDraggedColumn(column);
    e.dataTransfer.effectAllowed = "move";
    e.target.classList.add("opacity-50");
  };

  const handleDragEnd = (e) => {
    stopAutoScroll();
    setDraggedColumn(null);
    setDragOverColumn(null);
    e.target.classList.remove("opacity-50");
    document
      .querySelectorAll(".drag-over")
      .forEach((el) => el.classList.remove("drag-over"));
  };

  const handleDragOver = (e, column) => {
    e.preventDefault();
    handleAutoScroll(e);
    setDragOverColumn(column);
    document
      .querySelectorAll(".drag-over")
      .forEach((el) => el.classList.remove("drag-over"));
    e.currentTarget.classList.add("drag-over");
  };

  const handleDrop = (e, targetColumn) => {
    e.preventDefault();
    stopAutoScroll();

    if (!draggedColumn || draggedColumn.id === targetColumn.id) return;

    const newColumnOrder = [...columnOrder];
    const draggedIndex = newColumnOrder.indexOf(draggedColumn.id);
    const targetIndex = newColumnOrder.indexOf(targetColumn.id);

    newColumnOrder.splice(draggedIndex, 1);
    newColumnOrder.splice(targetIndex, 0, draggedColumn.id);

    setColumnOrder(newColumnOrder);
    setDragOverColumn(null);
  };

  const handleToggleAll = (event) => {
    table.toggleAllColumnsVisible(event.target.checked);
  };

  const handleResetOrder = () => {
    setColumnOrder(defaultColumnOrder);
  };

  const handleResetSticky = () => {
    table.setColumnPinning({ left: [], right: [] });
    setStickyColumnsState(table.options.state?.columnPinning);
  };

  const isAllVisible = useMemo(
    () => allColumns.every((column) => column.getIsVisible()),
    [allColumns]
  );
  const isSomeVisible = useMemo(
    () => allColumns.some((column) => column.getIsVisible()),
    [allColumns]
  );
  const isDefaultOrder =
    JSON.stringify(columnOrder) === JSON.stringify(defaultColumnOrder);

  return (
    <div
      className={`fixed right-0 top-0 h-full w-[400px] bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 flex flex-col
        ${isOpen ? "translate-x-0" : "translate-x-full"}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <h2 className="text-lg font-semibold">Table Preferences</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="hover:bg-gray-100 rounded-full p-2"
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Content - reuse existing content structure */}
      <div className="flex-1 overflow-hidden">
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Column Visibility</h3>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetOrder}
                disabled={isDefaultOrder}
                className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
              >
                Reset Order
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetSticky}
                disabled={
                  stickyColumnsState.left.length === 0 &&
                  stickyColumnsState.right.length === 0
                }
                className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
              >
                Reset Sticky
              </Button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="toggle-all"
              className="rounded border-gray-300 text-blue-500 focus:ring-blue-500 cursor-pointer"
              checked={isAllVisible}
              ref={(el) => {
                if (el) {
                  el.indeterminate = !isAllVisible && isSomeVisible;
                }
              }}
              onChange={handleToggleAll}
            />
            <label
              htmlFor="toggle-all"
              className="text-sm font-medium text-gray-700"
            >
              Toggle All
            </label>
          </div>

          <ScrollArea
            className="h-[calc(100vh-200px)] pr-4"
            ref={modalContentRef}
          >
            <div className="space-y-2">
              {sortedColumns.map((column) => {
                const id = `column-${column.id}`;
                const stickyStatus = stickyColumnsState.left.includes(column.id)
                  ? "left"
                  : stickyColumnsState.right.includes(column.id)
                  ? "right"
                  : null;

                return (
                  <div
                    key={column.id}
                    className={`flex items-center space-x-2 p-2 bg-white rounded-md border transition-all duration-200
                      ${
                        dragOverColumn?.id === column.id
                          ? "border-blue-400 bg-blue-50"
                          : "border-transparent hover:border-gray-200"
                      }
                      ${draggedColumn?.id === column.id ? "opacity-50" : ""}
                    `}
                    draggable
                    onDragStart={(e) => handleDragStart(e, column)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, column)}
                    onDrop={(e) => handleDrop(e, column)}
                  >
                    <div className="cursor-move">
                      <GripVertical className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="checkbox"
                      id={id}
                      className="rounded border-gray-300 text-blue-500 focus:ring-blue-500 cursor-pointer"
                      checked={column.getIsVisible()}
                      onChange={column.getToggleVisibilityHandler()}
                    />
                    <TooltipWrapper
                      tipText={column.columnDef?.meta?.columnInfo?.tip_text}
                    >
                      <label
                        htmlFor={id}
                        className="text-sm font-medium text-gray-700 flex-1"
                      >
                        {column.columnDef.meta?.columnInfo?.display}
                      </label>
                    </TooltipWrapper>

                    <div className="flex gap-1">
                      {!stickyStatus && (
                        <>
                          <button
                            onClick={() => {
                              table.setColumnPinning({
                                left: [...stickyColumnsState.left, column.id],
                                right: stickyColumnsState.right,
                              });
                              setStickyColumnsState({
                                left: [...stickyColumnsState.left, column.id],
                                right: stickyColumnsState.right,
                              });
                            }}
                            className="p-1 rounded hover:bg-gray-100"
                            title="Pin to left"
                          >
                            <ArrowLeftToLine className="h-4 w-4 text-gray-500" />
                          </button>
                          <button
                            onClick={() => {
                              table.setColumnPinning({
                                left: stickyColumnsState.left,
                                right: [...stickyColumnsState.right, column.id],
                              });
                              setStickyColumnsState({
                                left: stickyColumnsState.left,
                                right: [...stickyColumnsState.right, column.id],
                              });
                            }}
                            className="p-1 rounded hover:bg-gray-100"
                            title="Pin to right"
                          >
                            <ArrowRightToLine className="h-4 w-4 text-gray-500" />
                          </button>
                        </>
                      )}

                      {stickyStatus === "left" && (
                        <>
                          <span className="text-xs text-blue-600 px-2 py-1 bg-blue-100 rounded flex items-center">
                            <ArrowLeftToLine className="h-3 w-3 mr-1" /> Left
                            <button
                              onClick={() => {
                                table.setColumnPinning({
                                  left: stickyColumnsState.left.filter(
                                    (id) => id !== column.id
                                  ),
                                  right: stickyColumnsState.right,
                                });
                                setStickyColumnsState({
                                  left: stickyColumnsState.left.filter(
                                    (id) => id !== column.id
                                  ),
                                  right: stickyColumnsState.right,
                                });
                              }}
                              className="ml-1 rounded hover:bg-blue-200"
                              title="Remove sticky"
                            >
                              <X className="h-3 w-3 text-blue-600" />
                            </button>
                          </span>
                        </>
                      )}

                      {stickyStatus === "right" && (
                        <>
                          <span className="text-xs text-blue-600 px-2 py-1 bg-blue-100 rounded flex items-center">
                            <ArrowRightToLine className="h-3 w-3 mr-1" /> Right
                            <button
                              onClick={() => {
                                table.setColumnPinning({
                                  left: stickyColumnsState.left,
                                  right: stickyColumnsState.right.filter(
                                    (id) => id !== column.id
                                  ),
                                });
                                setStickyColumnsState({
                                  left: stickyColumnsState.left,
                                  right: stickyColumnsState.right.filter(
                                    (id) => id !== column.id
                                  ),
                                });
                              }}
                              className="ml-1 rounded hover:bg-blue-200"
                              title="Remove sticky"
                            >
                              <X className="h-3 w-3 text-blue-600" />
                            </button>
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Optional overlay for mobile/tablet */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
    </div>
  );
};

export default PreferencesPanel;
